import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { message } from "antd";
import axiosHttp from "services/axiosHttp";

export const downloadStockUploadTemplate = createAsyncThunk("stockCountUpload/downloadTemplate", async (_, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get("stock-take/stock-upload/template", {
			responseType: "blob",
		});

		const blob = new Blob([response.data], {
			type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});

		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", "stock_upload_template.xlsx");
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);

		message.success("Template downloaded successfully");
		return true;
	} catch (error) {
		const errorMessage = error.response?.data?.message || "Failed to download template";
		message.error(errorMessage);
		return rejectWithValue(error.response?.data || errorMessage);
	}
});

export const createStockUploadJob = createAsyncThunk("stockCountUpload/createJob", async ({ file }, { rejectWithValue }) => {
	try {
		const formData = new FormData();
		formData.append("file", file);

		const response = await axiosHttp.post("stock-take/stock-upload/job/create", formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});

		return response.data;
	} catch (error) {
		const errorMessage = error.response?.data?.message || "Failed to create stock upload job";
		message.error(errorMessage);
		return rejectWithValue(error.response?.data || errorMessage);
	}
});

export const fetchStockUploadJobStats = createAsyncThunk("stockCountUpload/fetchJobStats", async (jobId, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`stock-take/stock-upload/job/stats/${jobId}`);
		return response.data;
	} catch (error) {
		const errorMessage = error.response?.data?.message || "Failed to fetch job status";
		return rejectWithValue(error.response?.data || errorMessage);
	}
});

export const fetchStockUploadJobErrors = createAsyncThunk("stockCountUpload/fetchJobErrors", async ({ jobId, page = 1, limit = 100 }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`stock-take/stock-upload/job/errors/${jobId}?page=${page}&limit=${limit}`);
		return response.data;
	} catch (error) {
		const errorMessage = error.response?.data?.message || "Failed to fetch job errors";
		return rejectWithValue(error.response?.data || errorMessage);
	}
});

export const downloadStockUploadErrorLog = createAsyncThunk("stockCountUpload/downloadErrorLog", async ({ jobId }, { rejectWithValue }) => {
	try {
		const allErrors = [];
		const limit = 1000;
		let page = 1;
		let total = 0;

		while (true) {
			const response = await axiosHttp.get(`stock-take/stock-upload/job/errors/${jobId}?page=${page}&limit=${limit}`);
			const data = response?.data?.data || {};
			const docs = data?.docs || [];

			total = data?.total || total;
			allErrors.push(...docs);

			if (!docs.length || allErrors.length >= total) {
				break;
			}

			page += 1;
		}

		if (!allErrors.length) {
			message.info("No errors to download");
			return false;
		}

		const rows = allErrors.map((item) => {
			const input = item?.input || {};
			return {
				row: item?.row,
				stockCountId: input?.stockCountId ?? "",
				locationCode: input?.locationCode ?? "",
				subLocationCode: input?.subLocationCode ?? "",
				productCode: input?.productCode ?? "",
				batch: input?.batch ?? "",
				stock: input?.stock ?? "",
				typeId: input?.typeId ?? "",
				errors: (item?.errors || []).join(" | "),
			};
		});

		const headers = ["row", "stockCountId", "locationCode", "subLocationCode", "productCode", "batch", "stock", "typeId", "errors"];
		const csv = [
			headers.join(","),
			...rows.map((row) =>
				headers
					.map((header) => {
						const value = row[header] ?? "";
						return `"${String(value).replace(/"/g, '""')}"`;
					})
					.join(","),
			),
		].join("\n");

		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", "stock-upload-errors.csv");
		document.body.appendChild(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(url);

		message.success("Error log downloaded successfully");
		return true;
	} catch (error) {
		const errorMessage = error.response?.data?.message || "Failed to download error log";
		message.error(errorMessage);
		return rejectWithValue(error.response?.data || errorMessage);
	}
});

const stockCountUploadSlice = createSlice({
	name: "stockCountUpload",
	initialState: {
		templateLoading: false,
		jobCreateLoading: false,
		jobId: null,
		pollIntervalMs: 5000,
		jobStats: null,
		errorsData: {
			docs: [],
			total: 0,
			page: 1,
			limit: 100,
		},
		errorsLoading: false,
		errorLogLoading: false,
		error: null,
	},
	reducers: {
		clearUploadResult: (state) => {
			state.jobId = null;
			state.pollIntervalMs = 5000;
			state.jobStats = null;
			state.errorsData = {
				docs: [],
				total: 0,
				page: 1,
				limit: 100,
			};
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(downloadStockUploadTemplate.pending, (state) => {
				state.templateLoading = true;
			})
			.addCase(downloadStockUploadTemplate.fulfilled, (state) => {
				state.templateLoading = false;
			})
			.addCase(downloadStockUploadTemplate.rejected, (state, action) => {
				state.templateLoading = false;
				state.error = action.payload;
			})
			.addCase(createStockUploadJob.pending, (state) => {
				state.jobCreateLoading = true;
				state.error = null;
				state.jobStats = null;
				state.errorsData = {
					docs: [],
					total: 0,
					page: 1,
					limit: 100,
				};
			})
			.addCase(createStockUploadJob.fulfilled, (state, action) => {
				state.jobCreateLoading = false;
				state.jobId = action.payload?.data?.job_id || null;
				state.pollIntervalMs = action.payload?.data?.pollIntervalMs || 5000;
			})
			.addCase(createStockUploadJob.rejected, (state, action) => {
				state.jobCreateLoading = false;
				state.error = action.payload;
			})
			.addCase(fetchStockUploadJobStats.fulfilled, (state, action) => {
				state.jobStats = action.payload?.data || null;
			})
			.addCase(fetchStockUploadJobStats.rejected, (state, action) => {
				state.error = action.payload;
			})
			.addCase(fetchStockUploadJobErrors.pending, (state) => {
				state.errorsLoading = true;
			})
			.addCase(fetchStockUploadJobErrors.fulfilled, (state, action) => {
				state.errorsLoading = false;
				state.errorsData = {
					docs: action.payload?.data?.docs || [],
					total: action.payload?.data?.total || 0,
					page: action.payload?.data?.page || 1,
					limit: action.payload?.data?.limit || 100,
				};
			})
			.addCase(fetchStockUploadJobErrors.rejected, (state, action) => {
				state.errorsLoading = false;
				state.error = action.payload;
			})
			.addCase(downloadStockUploadErrorLog.pending, (state) => {
				state.errorLogLoading = true;
			})
			.addCase(downloadStockUploadErrorLog.fulfilled, (state) => {
				state.errorLogLoading = false;
			})
			.addCase(downloadStockUploadErrorLog.rejected, (state, action) => {
				state.errorLogLoading = false;
				state.error = action.payload;
			});
	},
});

export const { clearUploadResult } = stockCountUploadSlice.actions;

export default stockCountUploadSlice.reducer;
